import { MeasureDateRange } from "../types/fieldConfigurationTypes.ts";
import { z } from "@entrilia/shared/zod.ts";
import { ConditionEqualityType, type NumberFormatterOptions } from "../types/fieldConfigurationTypes.ts";

export const cashFlowSettingsSchema = z.object({
  splitBy: z.string().nullish(),
  splitFilter: z
    .object({
      dimensionName: z.string(),
      values: z.array(z.string().nullable()),
      equalityType: z.enum(ConditionEqualityType).optional(),
    })
    .nullish(),
  showSplitTotal: z.boolean().default(true),
  displayMode: z.enum(["Summary", "Details", "Both"]).default("Summary"),
  sections: z.enum(["Combined", "ContributionsAndDistributions", "InvestmentsAndProceeds"]).default("Combined"),
  showZeroTotals: z.boolean().default(true),
  expandTransactions: z.boolean().default(false),
  amountFormat: z.custom<NumberFormatterOptions>().nullish(),
});

export const reportSettingsSchema = z.object({
  detailedView: z.preprocess((value) => value ?? true, z.boolean()),
});

export const tabularGeneralSettingsSchema = reportSettingsSchema.extend({
  cashFlow: cashFlowSettingsSchema.nullish(),
  showGrandTotal: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  groupSummaryPlace: z
    .preprocess((val) => val ?? "Footer", z.union([z.literal("Footer"), z.literal("Inline")]))
    .catch("Footer"),
  subtotalLabelPosition: z
    .preprocess(
      (val) => val ?? "None",
      z.union([z.literal("None"), z.literal("Group"), z.literal("Leading"), z.literal("Trailing")])
    )
    .catch("None"),
  subtotalLabelFormatPrefix: z.preprocess((val) => val ?? "", z.string()).catch(""),
  subtotalLabelFormatSuffix: z.preprocess((val) => val ?? "", z.string()).catch(""),
  hideBlankRows: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideBlankColumns: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideBlankColumnsScope: z.preprocess((val) => val ?? "All", z.enum(["All", "Selected"])).catch("All"),
  hideBlankColumnIds: z.preprocess((val) => val ?? [], z.array(z.string())).catch([]),
  hideZeroRows: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideZeroColumns: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  showGroupedColumns: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  showNonZeroBalanceAccounts: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  freezedColumns: z.preprocess((val) => val ?? [], z.array(z.string())).catch([]),
});

export const pivotGeneralSettingsSchema = reportSettingsSchema.extend({
  rowsGrandTotal: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideBlankRows: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideZeroRows: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideBlankColumns: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideBlankColumnsScope: z.preprocess((val) => val ?? "All", z.enum(["All", "Selected"])).catch("All"),
  hideBlankColumnIds: z.preprocess((val) => val ?? [], z.array(z.string())).catch([]),
  hideZeroColumns: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  showGroupedColumns: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideGroupRows: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
});

export const tileSettingsSchema = reportSettingsSchema.extend({
  compareWithPreviousPeriod: z.boolean().default(false),
  showGrowth: z.boolean().default(false),
  periodType: z.enum(["LastMonth", "LastQuarter", "LastYear"]).default("LastMonth"),
});

export const listGeneralSettingsSchema = reportSettingsSchema.extend({
  showGrandTotal: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  freezedColumns: z.preprocess((val) => val ?? [], z.array(z.string())).catch([]),
  hideBlankRows: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideBlankRowsColumnIds: z.preprocess((val) => val ?? [], z.array(z.string())).catch([]),
});

export const financialStatementGeneralSettingsSchema = reportSettingsSchema.extend({
  hideBlankRows: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  hideZeroRows: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  highlightPeriod: z.preprocess((val) => val ?? false, z.boolean()).catch(false),
  showRowsGrandTotal: z.preprocess((val) => val ?? true, z.boolean()).catch(true),
  showNegativesInRed: z.preprocess((val) => val ?? true, z.boolean()).catch(true),
  defaultDateRange: z
    .preprocess((val) => val ?? MeasureDateRange.EndingBalance, z.enum(MeasureDateRange))
    .catch(MeasureDateRange.EndingBalance),
});
